import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribe } from 'lib/pusher';
import request from 'lib/request';
import { usePolling } from 'lib/hooks/usePolling';
import type { ProcessStatus, StartProcessResponse } from './types';

// Terminal = no more work will happen. overall_status flips to 'failed' the
// moment ANY step fails, but with fail_fast off the remaining steps keep
// draining — so also require that no steps are still pending/processing.
export const isTerminal = (s?: ProcessStatus | null) =>
  !!s &&
  (s.overall_status === 'processed' || s.overall_status === 'failed') &&
  s.pending_steps + s.processing_steps === 0;

// Builds the process handle for a process known only by id (e.g. a run-detail
// page loaded from its URL) — same shape a launch endpoint returns.
export const processRef = (processId: number): StartProcessResponse => ({
  processId,
  channel: `crm_workflow_process_${processId}`,
  statusHref: `/api/jobs/processes/${processId}`,
});

// Polls /api/jobs/processes/:id while a process is active; the Pusher channel
// nudges an immediate refresh. Polling stops once the process is terminal.
// Returns the latest status (or null).
export function useProcessStatus(
  proc: StartProcessResponse | null,
  onComplete?: (status: ProcessStatus) => void,
  // Called when polling gives up after repeated failures (a persistently broken
  // status endpoint), so the caller can surface it instead of a bar stuck on
  // "starting…" forever.
  onError?: () => void
): ProcessStatus | null {
  const [status, setStatus] = useState<ProcessStatus | null>(null);
  const completedRef = useRef(false);
  // Latest callbacks without re-running the effect (which would re-subscribe):
  // callers re-create these when their own deps change mid-poll.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  // Monotonic request id: a slow response that resolves after a newer one has
  // been issued is dropped, so the progress bar never jumps backward when the
  // poll tick and a Pusher nudge overlap. Never reset to 0 (only ever ++), so a
  // stale in-flight response from a previous proc can't collide with the new
  // proc's first request id.
  const seqRef = useRef(0);

  // Reset when the watched process changes.
  useEffect(() => {
    completedRef.current = false;
    setStatus(null);
  }, [proc?.processId]);

  const fetchStatus = useCallback(async () => {
    if (!proc) return;
    const seq = ++seqRef.current;
    const json = (await request.get(proc.statusHref)) as {
      data?: ProcessStatus;
      error?: unknown;
    };
    // A superseded response is ignored silently; the newer request will settle.
    if (seq !== seqRef.current) return;
    // request.get resolves an error as an { error } payload rather than
    // rejecting — throw so usePolling's retry/stop (maxRetries) engages instead
    // of polling a broken endpoint forever.
    if (json && typeof json === 'object' && 'error' in json) {
      throw new Error(String(json.error));
    }
    if (!json?.data) return;

    setStatus(json.data);
    if (isTerminal(json.data) && !completedRef.current) {
      completedRef.current = true;
      onCompleteRef.current?.(json.data);
    }
    // Keyed on statusHref (the only proc field read here): a new proc object
    // with the same href needn't rebuild the fetcher.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proc?.statusHref]);

  // usePolling waits for each request before scheduling the next (no overlap)
  // and pauses while the tab is hidden; it stops once the process is terminal,
  // and — via the throw above — after repeated status-endpoint failures.
  usePolling({
    fn: fetchStatus,
    intervalMs: 1500,
    enabled: !!proc && !isTerminal(status),
    immediate: true,
    onMaxRetriesReached: () => onErrorRef.current?.(),
  });

  // Pusher: an immediate refresh when the process reports a status change.
  useEffect(() => {
    if (!proc) return undefined;

    const channel = subscribe(proc.channel);
    channel.bind?.('crm-workflow-process-status-changed', fetchStatus);
    return () => {
      channel.unbind_all?.();
      // Close the subscription too — channels are per-process, so moving
      // between runs would otherwise pile up live subscriptions.
      channel.unsubscribe?.();
    };
    // Re-subscribes only when the channel changes; proc's other fields are
    // irrelevant to the subscription.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proc?.channel, fetchStatus]);

  return status;
}
